import type { ElementType, HTMLAttributes, ReactNode } from 'react';

/**
 * Every slug from the Figma typography catalog.
 * Maps 1:1 to a `.spark-text-{variant}` utility class.
 */
export type TextVariant =
  | 'display-light'
  | 'display-medium'
  | 'display-black'
  | 'heading-1-semibold'
  | 'heading-2-semibold'
  | 'heading-3-semibold'
  | 'heading-3-medium'
  | 'heading-4-semibold'
  | 'heading-4-medium'
  | 'heading-5-semibold'
  | 'heading-5-medium'
  | 'lead-xl-regular'
  | 'lead-regular'
  | 'lead-medium'
  | 'lead-semibold'
  | 'lead-bold'
  | 'base-regular'
  | 'base-medium'
  | 'base-semibold'
  | 'detail-regular'
  | 'detail-medium'
  | 'detail-semibold'
  | 'uniq-regular-button-link'
  | 'uniq-semibold-button-link-small'
  | 'uniq-semibold-table-caps'
  | 'uniq-regular-table'
  | 'uniq-medium-button-link'
  | 'uniq-semibold-button-link-medium';

export type TextColor = 'primary' | 'secondary' | 'tertiary' | 'inherit';

export type TextAs =
  | 'p'
  | 'span'
  | 'div'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'label'
  | 'strong'
  | 'em';

/** Typography wrapper applying a Spark text-style variant and optional content color via utility classes. */
export type TextProps = {
  /** Figma text-style slug. Controls font-family, font-size, line-height, and font-weight. */
  variant: TextVariant;
  /** Content color. Defaults to `inherit` to not override surrounding color. */
  color?: TextColor;
  /** HTML element to render. Defaults to `span`. */
  as?: TextAs;
  children?: ReactNode;
  className?: string;
} & Omit<HTMLAttributes<HTMLElement>, 'color'>;

/**
 * Thin wrapper that applies a Spark typography text-style and optional content
 * color via utility classes. Use instead of hand-rolling font properties.
 *
 * @example
 * <Text variant="base-semibold" color="primary" as="p">Files ({n})</Text>
 * <Text variant="base-regular" color="secondary">Subtitle text</Text>
 */
export function Text({
  variant,
  color = 'inherit',
  as = 'span',
  className = '',
  children,
  ...rest
}: TextProps) {
  const colorClass = color === 'inherit' ? '' : `spark-content-${color}`;
  const rootClass = [`spark-text-${variant}`, colorClass, className]
    .filter(Boolean)
    .join(' ');

  const Tag = as as ElementType;
  return (
    <Tag className={rootClass} {...rest}>
      {children}
    </Tag>
  );
}
